import { useAsync } from '@nxsys/hooks/useAsync';
import {
  checkSubDepartmentAssignApi,
  connectNestPensionApi,
  connectNestPensionDraftApi,
  connectSmartPensionApi,
  connectSmartPensionDraftApi,
  connectZohoApi,
  getZohoTemplatesApi,
  getZohoTemplateDetailsApi,
  createBannerApi,
  createClientApplicationApi,
  createCompanyApi,
  createCompanyDraftApi,
  createExpenseCompanyApi,
  createPrivateIrelandPensionApi,
  createWorkingPatternApi,
  deleteBannerApi,
  deleteExpenseCompanyApi,
  deletePrivateIrelandPensionApi,
  deleteWorkingPatternApi,
  disconnectNestPensionApi,
  disconnectSmartPensionApi,
  getClientApplicationApi,
  getCompanyByIdApi,
  getCompanyDraftPensionStatusApi,
  getEmailTemplateByIdApi,
  getNestSettingApi,
  getPensionProvidersApi,
  getPrivateIrelandPensionApi,
  getZohoSettingsApi,
  sendCompanyDraftLinkApi,
  sendTestEmailApi,
  sendTestEmailV2Api,
  updateBannerApi,
  updateBoostworksSettingsApi,
  updateClientApplicationApi,
  updateCompanyByIdApi,
  updateCompanyStatusApi,
  updateEmailTemplateApi,
  updateExpenseCompanyApi,
  updateNestSettingApi,
  updatePrivateIrelandPensionApi,
  updateSafeRecSettingsApi,
  updateVeriPayeSettingsApi,
  updateWorkingPatternApi,
  updateZohoSettingsApi,
  toggleDefaultPrivateIrelandPensionApi,
  getCompanyTimesheetValueByIdApi,
  createCompanyTimesheetValueByIdApi,
} from '@nxsys/services/api/company';
import type {
  GetAgencyTimesheetValueByIdParams,
  GetAgencyTimesheetValueByIdResponse,
  CreateAgencyTimesheetValueByIdParams,
  CreateAgencyTimesheetValueByIdResponse,
} from '@nxsys/types/api';
import {
  CheckSubDepartmentAssignParams,
  CheckSubDepartmentAssignResponse,
  ConnectNestPensionParams,
  ConnectNestPensionResponse,
  ConnectSmartPensionParams,
  ConnectSmartPensionResponse,
  ConnectZohoResponse,
  CreateBannerParams,
  CreateBannerResponse,
  CreateClientApplicationParams,
  CreateClientApplicationResponse,
  CreateCompanyDraftParams,
  CreateCompanyDraftResponse,
  GetCompanyDraftPensionStatusResponse,
  CreateCompanyParams,
  CreateCompanyResponse,
  CreateExpenseCompanyParams,
  CreateExpenseCompanyResponse,
  CreatePrivateIrelandPensionParams,
  CreatePrivateIrelandPensionResponse,
  CreateWorkingPatternParams,
  CreateWorkingPatternResponse,
  DeleteBannerResponse,
  DeleteExpenseCompanyResponse,
  DeletePrivateIrelandPensionResponse,
  DeleteWorkingPatternResponse,
  DisconnectNestPensionResponse,
  DisconnectSmartPensionResponse,
  GetClientApplicationParams,
  GetClientApplicationResponse,
  GetCompanyByIdResponse,
  GetEmailTemplateByIdResponse,
  GetNestSettingResponse,
  GetPensionProvidersResponse,
  GetPrivateIrelandPensionResponse,
  GetZohoSettingsResponse,
  SendTestEmailParams,
  SendTestEmailResponse,
  UpdateBannerParams,
  UpdateBannerResponse,
  UpdateBoostworksSettingsParams,
  UpdateBoostworksSettingsResponse,
  UpdateClientApplicationParams,
  UpdateClientApplicationResponse,
  UpdateCompanyByIdParams,
  UpdateCompanyByIdResponse,
  UpdateEmailTemplateParams,
  UpdateEmailTemplateResponse,
  UpdateExpenseCompanyParams,
  UpdateExpenseCompanyResponse,
  UpdateNestSettingParams,
  UpdateNestSettingResponse,
  UpdatePrivateIrelandPensionParams,
  UpdatePrivateIrelandPensionResponse,
  UpdateSafeRecSettingsParams,
  UpdateSafeRecSettingsResponse,
  UpdateVeriPayeSettingsParams,
  UpdateVeriPayeSettingsResponse,
  UpdateWorkingPatternParams,
  UpdateWorkingPatternResponse,
  UpdateZohoSettingsParams,
  UpdateZohoSettingsResponse,
  ConnectSmartPensionDraftResponse,
  SendCompanyDraftLinkParams,
  SendCompanyDraftLinkResponse,
} from '@nxsys/types/api/company';
import { IPrivateIrelandPension } from '@nxsys/types/models/pension';
import { showMessage } from '@nxsys/utils/message';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useGetCompanyById = () => {
  const { value, loading, error, run } = useAsync<
    GetCompanyByIdResponse,
    string
  >(getCompanyByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateCompanyById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateCompanyByIdResponse,
    UpdateCompanyByIdParams & { id: string }
  >(({ id, ...params }) => updateCompanyByIdApi(id, params));

  const _run = (id: string, params: UpdateCompanyByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateExpenseCompany = () => {
  const { value, loading, error, run } = useAsync<
    CreateExpenseCompanyResponse,
    CreateExpenseCompanyParams & { companyId: string }
  >(({ companyId, ...params }) => createExpenseCompanyApi(companyId, params));

  const _run = (companyId: string, params: CreateExpenseCompanyParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateExpenseCompany = () => {
  const { value, loading, error, run } = useAsync<
    UpdateExpenseCompanyResponse,
    UpdateExpenseCompanyParams & { companyId: string; expenseId: string }
  >(({ companyId, expenseId, ...params }) =>
    updateExpenseCompanyApi(companyId, expenseId, params),
  );

  const _run = (
    companyId: string,
    expenseId: string,
    params: UpdateExpenseCompanyParams,
  ) => {
    return run({ companyId, expenseId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDeleteExpenseCompany = () => {
  const { value, loading, error, run } = useAsync<
    DeleteExpenseCompanyResponse,
    { companyId: string; expenseId: string }
  >(({ companyId, expenseId }) =>
    deleteExpenseCompanyApi(companyId, expenseId),
  );

  const _run = (companyId: string, expenseId: string) => {
    return run({ companyId, expenseId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetPensionProviders = () => {
  const { value, loading, error, run } = useAsync<
    GetPensionProvidersResponse,
    string
  >(getPensionProvidersApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useConnectSmartPension = () => {
  const { value, loading, error, run } = useAsync<
    ConnectSmartPensionResponse,
    ConnectSmartPensionParams & { companyId: string }
  >(({ companyId, ...params }) => connectSmartPensionApi(companyId, params));

  const _run = (companyId: string, params: ConnectSmartPensionParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDisconnectSmartPension = () => {
  const { value, loading, error, run } = useAsync<
    DisconnectSmartPensionResponse,
    string
  >(disconnectSmartPensionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useConnectNestPension = () => {
  const { value, loading, error, run } = useAsync<
    ConnectNestPensionResponse,
    ConnectNestPensionParams & { companyId: string }
  >(({ companyId, ...params }) => connectNestPensionApi(companyId, params));

  const _run = (companyId: string, params: ConnectNestPensionParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetNestSetting = () => {
  const { value, loading, error, run } = useAsync<
    GetNestSettingResponse,
    string
  >(getNestSettingApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDisconnectNestPension = () => {
  const { value, loading, error, run } = useAsync<
    DisconnectNestPensionResponse,
    string
  >(disconnectNestPensionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateNestSetting = () => {
  const { value, loading, error, run } = useAsync<
    UpdateNestSettingResponse,
    UpdateNestSettingParams & { companyId: string }
  >(({ companyId, ...params }) => updateNestSettingApi(companyId, params));

  const _run = (companyId: string, params: UpdateNestSettingParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetZohoSettings = () => {
  const { value, loading, error, run } = useAsync<
    GetZohoSettingsResponse,
    string
  >(getZohoSettingsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateZohoSettings = () => {
  const { value, loading, error, run } = useAsync<
    UpdateZohoSettingsResponse,
    UpdateZohoSettingsParams & { companyId: string }
  >(({ companyId, ...params }) => updateZohoSettingsApi(companyId, params));

  const _run = (companyId: string, params: UpdateZohoSettingsParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useConnectZoho = () => {
  const { value, loading, error, run } = useAsync<ConnectZohoResponse, string>(
    connectZohoApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetZohoTemplates = () => {
  const { value, loading, error, run } = useAsync<
    { templates: any[] },
    string
  >(getZohoTemplatesApi);

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useGetZohoTemplateDetails = () => {
  const { value, loading, error, run } = useAsync<
    { templates: any },
    { companyId: string; templateId: string }
  >(({ companyId, templateId }) =>
    getZohoTemplateDetailsApi(companyId, templateId),
  );

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  const _run = (companyId: string, templateId: string) =>
    run({ companyId, templateId });

  return { value, loading, error, run: _run };
};

export const useUpdateSafeRecSettings = () => {
  const { value, loading, error, run } = useAsync<
    UpdateSafeRecSettingsResponse,
    UpdateSafeRecSettingsParams & { companyId: string }
  >(({ companyId, ...params }) => updateSafeRecSettingsApi(companyId, params));

  const _run = (companyId: string, params: UpdateSafeRecSettingsParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateBoostworksSettings = () => {
  const { value, loading, error, run } = useAsync<
    UpdateBoostworksSettingsResponse,
    UpdateBoostworksSettingsParams & { companyId: string }
  >(({ companyId, ...params }) =>
    updateBoostworksSettingsApi(companyId, params),
  );

  const _run = (companyId: string, params: UpdateBoostworksSettingsParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateVeriPayeSettings = () => {
  const { value, loading, error, run } = useAsync<
    UpdateVeriPayeSettingsResponse,
    UpdateVeriPayeSettingsParams & { companyId: string }
  >(({ companyId, ...params }) => updateVeriPayeSettingsApi(companyId, params));

  const _run = (companyId: string, params: UpdateVeriPayeSettingsParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useSendTestEmail = () => {
  const { value, loading, error, run } = useAsync<
    SendTestEmailResponse,
    SendTestEmailParams & { id: string }
  >(({ id, ...params }) => sendTestEmailApi(id, params));

  const _run = (id: string, params: SendTestEmailParams) => {
    return run({ id, ...params });
  };

  // No toast here: the only caller reports the failure itself, next to the
  // in-band { success: false } case it also has to handle.

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetEmailTemplateById = () => {
  const { value, loading, error, run } = useAsync<
    GetEmailTemplateByIdResponse,
    string
  >(getEmailTemplateByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateEmailTemplate = () => {
  const { value, loading, error, run } = useAsync<
    UpdateEmailTemplateResponse,
    UpdateEmailTemplateParams & { id: string }
  >(({ id, ...params }) => updateEmailTemplateApi(id, params));

  const _run = (id: string, params: UpdateEmailTemplateParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetClientApplication = () => {
  const { value, loading, error, run } = useAsync<
    GetClientApplicationResponse,
    GetClientApplicationParams
  >(getClientApplicationApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreateClientApplication = () => {
  const { value, loading, error, run } = useAsync<
    CreateClientApplicationResponse,
    CreateClientApplicationParams
  >(createClientApplicationApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateClientApplication = () => {
  const { value, loading, error, run } = useAsync<
    UpdateClientApplicationResponse,
    UpdateClientApplicationParams & { id: number }
  >(({ id, ...params }) => updateClientApplicationApi(id, params));

  const _run = (id: number, params: UpdateClientApplicationParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateBanner = () => {
  const { value, loading, error, run } = useAsync<
    CreateBannerResponse,
    CreateBannerParams
  >(createBannerApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateBanner = () => {
  const { value, loading, error, run } = useAsync<
    UpdateBannerResponse,
    UpdateBannerParams & { id: number }
  >(({ id, ...params }) => updateBannerApi(id, params));

  const _run = (id: number, params: UpdateBannerParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDeleteBanner = () => {
  const { value, loading, error, run } = useAsync<DeleteBannerResponse, number>(
    deleteBannerApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreateWorkingPattern = () => {
  const { value, loading, error, run } = useAsync<
    CreateWorkingPatternResponse,
    CreateWorkingPatternParams
  >(createWorkingPatternApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateWorkingPattern = () => {
  const { value, loading, error, run } = useAsync<
    UpdateWorkingPatternResponse,
    UpdateWorkingPatternParams & { id: number }
  >(({ id, ...params }) => updateWorkingPatternApi(id, params));

  const _run = (id: number, params: UpdateWorkingPatternParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDeleteWorkingPattern = () => {
  const { value, loading, error, run } = useAsync<
    DeleteWorkingPatternResponse,
    number
  >(deleteWorkingPatternApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCheckSubDepartmentAssign = () => {
  const { value, loading, error, run } = useAsync<
    CheckSubDepartmentAssignResponse,
    CheckSubDepartmentAssignParams
  >(checkSubDepartmentAssignApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPrivateIrelandPension = () => {
  const { value, loading, error, run } = useAsync<
    GetPrivateIrelandPensionResponse,
    string
  >(getPrivateIrelandPensionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreatePrivateIrelandPension = () => {
  const { value, loading, error, run } = useAsync<
    CreatePrivateIrelandPensionResponse,
    CreatePrivateIrelandPensionParams & { companyId: string }
  >(({ companyId, ...params }) =>
    createPrivateIrelandPensionApi(companyId, params),
  );

  const _run = (
    companyId: string,
    params: CreatePrivateIrelandPensionParams,
  ) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdatePrivateIrelandPension = () => {
  const { value, loading, error, run } = useAsync<
    UpdatePrivateIrelandPensionResponse,
    UpdatePrivateIrelandPensionParams & { companyId: string; pensionId: number }
  >(({ companyId, pensionId, ...params }) =>
    updatePrivateIrelandPensionApi(companyId, pensionId, params),
  );

  const _run = (
    companyId: string,
    pensionId: number,
    params: UpdatePrivateIrelandPensionParams,
  ) => {
    return run({ companyId, pensionId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDeletePrivateIrelandPension = () => {
  const { value, loading, error, run } = useAsync<
    DeletePrivateIrelandPensionResponse,
    { companyId: string; pensionId: number }
  >(({ companyId, pensionId }) =>
    deletePrivateIrelandPensionApi(companyId, pensionId),
  );

  const _run = (companyId: string, pensionId: number) => {
    return run({ companyId, pensionId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useToggleDefaultPrivateIrelandPension = () => {
  const { value, loading, error, run } = useAsync<
    IPrivateIrelandPension,
    { companyId: string; settingId: number }
  >(({ companyId, settingId }) =>
    toggleDefaultPrivateIrelandPensionApi(companyId, settingId),
  );

  const _run = (companyId: string, settingId: number) => {
    return run({ companyId, settingId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useUpdateCompanyStatus = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean; message: string },
    { isActive: boolean; id: string }
  >((params) => updateCompanyStatusApi(params.id, params));

  const _run = (id: string, params: { isActive: boolean }) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateCompany = () => {
  const { value, loading, error, run } = useAsync<
    CreateCompanyResponse,
    CreateCompanyParams
  >(createCompanyApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreateCompanyDraft = () => {
  const { value, loading, error, run } = useAsync<
    CreateCompanyDraftResponse,
    CreateCompanyDraftParams
  >(createCompanyDraftApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useConnectNestPensionDraft = () => {
  const { value, loading, error, run } = useAsync<
    ConnectNestPensionResponse,
    ConnectNestPensionParams & { companyId: number }
  >(({ companyId, ...params }) => connectNestPensionDraftApi(companyId, params));

  const _run = (companyId: number, params: ConnectNestPensionParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useConnectSmartPensionDraft = () => {
  const { value, loading, error, run } = useAsync<
    ConnectSmartPensionDraftResponse,
    ConnectSmartPensionParams & { companyId: number }
  >(({ companyId, ...params }) => connectSmartPensionDraftApi(companyId, params));

  const _run = (companyId: number, params: ConnectSmartPensionParams) => {
    return run({ companyId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useSendCompanyDraftLink = () => {
  const { value, loading, error, run } = useAsync<
    SendCompanyDraftLinkResponse,
    SendCompanyDraftLinkParams & { draftId: number }
  >(({ draftId, ...params }) => sendCompanyDraftLinkApi(draftId, params));

  const _run = (draftId: number, params: SendCompanyDraftLinkParams) => {
    return run({ draftId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30000;

export const useGetCompanyDraftPensionStatus = () => {
  const [status, setStatus] = useState<GetCompanyDraftPensionStatusResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    intervalRef.current = null;
    timeoutRef.current = null;
    setPolling(false);
  }, []);

  const startPolling = useCallback((draftId: number) => {
    setError(null);
    setPolling(true);

    const poll = async () => {
      try {
        const res = await getCompanyDraftPensionStatusApi(draftId);
        setStatus(res);
        if (
          res.smartPension.status === 'AUTHORIZED' ||
          res.smartPension.status === 'ERROR'
        ) {
          stopPolling();
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to get pension status');
        stopPolling();
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(async () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      try {
        const res = await getCompanyDraftPensionStatusApi(draftId);
        setStatus(res);
        if (!res?.smartPension?.authorized) {
          setError('Smart Pension authorization timed out');
        }
      } catch {
        // keep last known status
      } finally {
        setPolling(false);
        timeoutRef.current = null;
      }
    }, POLL_TIMEOUT_MS);
  }, [stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { status, polling, error, startPolling, stopPolling };
};

export const useSendTestEmailV2 = () => {
  const { value, loading, error, run } = useAsync<
    SendTestEmailResponse,
    SendTestEmailParams
  >(sendTestEmailV2Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

// Company-level margin band table. Deleting a row uses the shared
// `/timesheet-values/:id` endpoint, see useDeleteAgencyTimesheetValueById.
export const useGetCompanyTimesheetValueById = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyTimesheetValueByIdResponse,
    [string, GetAgencyTimesheetValueByIdParams]
  >(([id, params]) => getCompanyTimesheetValueByIdApi(id, params));

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCreateCompanyTimesheetValueById = () => {
  const { value, loading, error, run } = useAsync<
    CreateAgencyTimesheetValueByIdResponse,
    CreateAgencyTimesheetValueByIdParams & { id: string }
  >(({ id, ...params }) => createCompanyTimesheetValueByIdApi(id, params));

  const _run = async (
    id: string,
    params: CreateAgencyTimesheetValueByIdParams,
  ) => run({ id, ...params });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};
